import { Button, TableFooter, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import StorefrontIcon from "@material-ui/icons/Storefront";
import { CSSProperties } from "@material-ui/styles";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { MallListDropOff_malls_edges_node } from "@saleor/dropOff/types/MallListDropOff";
import { dropOffListPath } from "@saleor/dropOff/url";
import { TabValuesEnum } from "@saleor/dropOff/utils/filters";
import useNavigator from "@saleor/hooks/useNavigator";
import { renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import { LogisticsStatusEnum } from "@saleor/types/globalTypes";
import { stringify as stringifyQs } from "qs";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import urlJoin from "url-join";

export interface DropOffTemplatesTableProps extends ListProps {
  dropOffList: MallListDropOff_malls_edges_node[];
}

const useStyles = makeStyles(
  theme => {
    const dataDisplay: CSSProperties = {
      flex: "60%",
      overflow: "auto",
      textAlign: "left"
    };

    return {
      buttonColor: {
        backgroundColor: "#0030FF",
        color: "#FFF",
        ...{
          "&:hover": {
            backgroundColor: "#0030FF"
          }
        }
      },
      dataGroup: {
        height: "100%"
      },
      dataGroupAction: {
        display: "inline-block !important",
        marginTop: "10px"
      },
      dataMallName: {
        color: "#0030ff",
        fontWeight: 600
      },
      dataTaskArea: {
        alignItems: "center",
        display: "flex",
        height: "40px",
        justifyContent: "space-between"
      },
      dataTaskAreaText: {
        fontWeight: 300,
        maxWidth: "250px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      },
      dataTitle: {
        display: "none !important"
      },
      dropOffList: {
        [theme.breakpoints.down("md")]: {
          "& thead": {
            display: "none !important"
          },
          "& thead, tbody, th, td, tr": {
            border: "none",
            display: "block"
          },
          "& tr": {
            backgroundColor: "#fff !important",
            borderBottom: "1px solid #ccc",
            paddingBottom: "30px",
            paddingTop: "30px"
          }
        },
        tableLayout: "auto",
        width: "100%"
      },
      responsiveTable: {
        overflow: "auto !important"
      },
      [theme.breakpoints.down("md")]: {
        dataDisplay,
        dataGroup: {
          display: "flex"
        },
        dataTitle: {
          display: "block !important",
          flex: "40%",
          fontWeight: "bold",
          textAlign: "left"
        }
      },
      tblCol: {
        fontSize: "1.3em",
        fontWeight: 600,
        whiteSpace: "nowrap",
        width: "auto"
      },
      tblColHeader: {
        fontWeight: 500,
        padding: "25px"
      },
      textButton: {
        color: "#FFF"
      }
    };
  },
  {
    name: "DropOffTemplatesTable"
  }
);

const DropOffTemplatesTable: React.FC<DropOffTemplatesTableProps> = props => {
  const {
    dropOffList,
    settings,
    onNextPage,
    onPreviousPage,
    pageInfo,
    disabled,
    onUpdateListSettings
  } = props;

  const classes = useStyles(props);
  const navigate = useNavigator();
  const numberOfColumns = 6;

  return (
    <div className={classes.responsiveTable}>
      <Table className={classes.dispatchTbl}>
        <TableHead>
          <TableRow>
            <TableCellHeader
              className={`${classes.tblCol} ${classes.tblColHeader}`}
            >
              <FormattedMessage defaultMessage="Mall Name" />
            </TableCellHeader>
            <TableCellHeader
              className={`${classes.tblCol} ${classes.tblColHeader}`}
            >
              <FormattedMessage defaultMessage="Task Areas" />
            </TableCellHeader>
            <TableCellHeader
              className={`${classes.tblCol} ${classes.tblColHeader}`}
            >
              <FormattedMessage defaultMessage="Stores" />
            </TableCellHeader>
            <TableCellHeader
              className={`${classes.tblCol} ${classes.tblColHeader}`}
            >
              <FormattedMessage defaultMessage="Home Location" />
            </TableCellHeader>
            <TableCellHeader
              className={`${classes.tblCol} ${classes.tblColHeader}`}
            >
              <FormattedMessage defaultMessage="Templates" />
            </TableCellHeader>
            <TableCellHeader
              className={`${classes.tblCol} ${classes.tblColHeader}`}
            >
              <FormattedMessage defaultMessage="Actions" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderCollection(
            dropOffList,
            (mall: MallListDropOff_malls_edges_node) => (
              <TableRow>
                <TableCell>
                  <div className={`${classes.dataGroup}`}>
                    <div
                      className={`${classes.dataDisplay} ${classes.dataMallName}`}
                    >
                      {mall?.name}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.dataGroup}>
                    <div className={classes.dataDisplay}>
                      {mall?.dropOffLocations?.edges?.map(e => (
                        <>
                          {e?.node?.taskAreas?.map(e => (
                            <div key={e?.id} className={classes.dataTaskArea}>
                              <h4 className={classes.dataTaskAreaText}>
                                {e?.name}
                              </h4>
                            </div>
                          ))}
                        </>
                      ))}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.dataGroup}>
                    <div className={classes.dataDisplay}>
                      {mall?.dropOffLocations?.edges?.map(e => (
                        <>
                          {e?.node?.taskAreas?.map(e => (
                            <div key={e?.id} className={classes.dataTaskArea}>
                              <h4 className={classes.dataTaskAreaText}>
                                {e?.tenants?.totalCount + " Stores"}
                              </h4>
                              <StorefrontIcon
                                className={classes.dataMallName}
                              />
                            </div>
                          ))}
                        </>
                      ))}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.dataGroup}>
                    <div className={classes.dataDisplay}>
                      {mall?.dropOffLocations?.edges
                        .filter(
                          e => e.node.status !== LogisticsStatusEnum.DELETED
                        )
                        .map(e => (
                          <div
                            key={e?.node?.id}
                            className={classes.dataTaskArea}
                          >
                            <h4 className={classes.dataTaskAreaText}>
                              {e?.node?.name}
                            </h4>
                          </div>
                        ))}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.dataGroup}>
                    <div className={classes.dataDisplay}>
                      <div className={classes.dataTaskArea}>
                        <h4 className={classes.dataTaskAreaText}>
                          {mall?.templates?.totalCount &&
                          mall?.templates?.totalCount > 0
                            ? mall?.templates?.totalCount + " Templates"
                            : "No Templates"}
                        </h4>
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    className={`${classes.dataGroup} ${classes.dataGroupAction}`}
                  >
                    <Button
                      className={classes.buttonColor}
                      onClick={() => {
                        navigate(
                          urlJoin(
                            dropOffListPath,
                            "?" +
                              stringifyQs({
                                mall: mall.id,
                                tab: TabValuesEnum.TEMPLATES
                              })
                          )
                        );
                      }}
                    >
                      <div className={classes.textButton}>View</div>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns} padding="default">
                  <Typography variant="h5" align="center">
                    No Data
                  </Typography>
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};

export default DropOffTemplatesTable;
